import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useActiveCompany, pickActiveYear } from "../../context/ActiveCompanyContext";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const YEARS = Array.from({ length: 2040 - 2022 + 1 }, (_, i) => 2022 + i);

export default function WorkingHoursPage() {
  const { isAdmin } = useAuth();
  const { year: activeYear, setYear } = useActiveCompany();
  const year = activeYear ?? new Date().getFullYear();
  const [months, setMonths] = useState([]);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    const res = await api.get("/working-hours", { params: { year } });
    setMonths(res.data);
    setDrafts({});
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const save = async (month) => {
    const value = Number(drafts[month]);
    try {
      await api.put("/working-hours", { year, month, available_hours: value });
      toast.success(`${MONTHS[month - 1]} ${year} saved`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="working-hours-page">
      <PageHeader
        title="Working Hours"
        subtitle="Calendar available hours per month — Group-wide, shared across all companies"
        actions={
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="working-hours-year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YEARS.map((y) => (
                <SelectItem key={y} value={String(y)} data-testid={`working-hours-year-${y}`}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden max-w-3xl" data-testid="working-hours-table">
        <table className="w-full">
          <thead>
            <tr className="bg-secondary/50 border-b border-slate-200">
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Month</th>
              <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Available hours</th>
              {isAdmin && <th className="py-3 px-4" />}
            </tr>
          </thead>
          <tbody>
            {months.map((m) => (
              <tr key={m.month} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`working-hours-row-${m.month}`}>
                <td className="py-2.5 px-4 text-sm font-semibold text-slate-800">{MONTHS[m.month - 1]}</td>
                <td className="py-2.5 px-4">
                  {isAdmin ? (
                    <Input
                      type="number"
                      min="1"
                      className="h-8 w-32"
                      value={drafts[m.month] ?? m.available_hours ?? ""}
                      onChange={(e) => setDrafts({ ...drafts, [m.month]: e.target.value })}
                      data-testid={`working-hours-input-${m.month}`}
                    />
                  ) : (
                    <span className="text-sm text-slate-700" data-testid={`working-hours-value-${m.month}`}>
                      {m.available_hours ?? "—"}
                    </span>
                  )}
                </td>
                {isAdmin && (
                  <td className="py-2.5 px-4 text-right">
                    {drafts[m.month] !== undefined && (
                      <Button size="sm" className="h-8 bg-[#0052CC] hover:bg-[#0047B3]" onClick={() => save(m.month)} data-testid={`working-hours-save-${m.month}`}>
                        Save
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
